import * as React from 'react'
import { useRouter } from '@tanstack/react-router'
import type { LinkComponentProps, RegisteredRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { PathHelperContext } from '@/hooks/path-helper-context'

export { usePathHelper } from '@/hooks/path-helper-context'

// Legacy redirect routes can share a final URL segment with the canonical page.
// Prefer the real destination so helpers like path.domains.index() do not send
// new links through compatibility redirects.
const preferredHelperBaseRouteSuffixes: Partial<Record<string, readonly string[]>> = {
  customerBalances: ['/money/customer-balances/', '/money/customer-balances'],
  sales: ['/reports/sales/', '/reports/sales'],
  statements: ['/money/statements/', '/money/statements'],
  domains: ['/website/domains/', '/website/domains'],
  condolences: ['/settings/integrations/condolences/', '/settings/integrations/condolences'],
  fsnNetwork: ['/settings/integrations/fsn-network/', '/settings/integrations/fsn-network'],
  googleAnalytics: [
    '/settings/integrations/google-analytics/',
    '/settings/integrations/google-analytics',
  ],
  googleTagManager: [
    '/settings/integrations/google-tag-manager/',
    '/settings/integrations/google-tag-manager',
  ],
  metaPixel: ['/settings/integrations/meta-pixel/', '/settings/integrations/meta-pixel'],
  snapdragon: ['/settings/integrations/snapdragon/', '/settings/integrations/snapdragon'],
  cashDrawer: ['/reports/cash-drawer/', '/reports/cash-drawer'],
}

const aliasedHelperBaseRouteSuffixes = {
  cashDrawerSettings: ['/settings/cash-drawer/', '/settings/cash-drawer'],
} satisfies Record<string, readonly string[]>

export type RouteParamValue = string | number
// JavaScript callers can pass an undefined id while data loads or a nullable API id; it stays
// in params and the href keeps String(value), exactly as before.
export type RouteParams = Record<string, RouteParamValue | null | undefined>
// `undefined` (or `null`) clears a search value in TanStack's updater; the href keeps String(value),
// as URLSearchParams did.
export type PathSearchParams = Record<string, string | number | boolean | null | undefined>

export type PathLink = {
  to: string
  params: RouteParams
  search?: (previous: Record<string, unknown>) => Record<string, unknown>
}

// Link props shared components accept, so path-helper links (`path.customers.show(id).link`) can be
// passed straight through. Helper links use the router's own route paths, which include index-route
// trailing slashes that Link's strict `to` list leaves out, so `to` is a string; <Link> accepts one.
// params/search take every form Link accepts for a string `to`: an object, `true` (keep the current
// values) or a reducer from the current values. (Link's own params/search types assume a route-typed
// `to`, so they can't be reused here.)
export type AppLinkProps = Omit<LinkComponentProps, 'to' | 'params' | 'search'> & {
  to?: string
  params?: RouteParams | true | ((previous: Record<string, unknown>) => Record<string, unknown>)
  search?:
    | Record<string, unknown>
    | true
    | ((previous: Record<string, unknown>) => Record<string, unknown>)
}

export class PathResult {
  link: PathLink
  href: string
  route: string

  constructor(link: PathLink, href: string, route: string) {
    this.link = link
    this.href = href
    this.route = route

    this.using = this.using.bind(this)
    this.search = this.search.bind(this)
  }

  using(params: RouteParams): this {
    this.link = {
      ...this.link,
      params: {
        ...this.link.params,
        ...params,
      },
    }

    let newHref = this.link.to

    Object.entries(this.link.params).forEach(([key, value]) => {
      newHref = newHref.replace(`$${key}`, String(value))
    })

    this.href = newHref
    return this
  }

  // search can be used to add search params (query string) to the path
  search(params: PathSearchParams): this {
    this.link = {
      ...this.link,
      search: (oldSearch) => ({
        ...oldSearch,
        ...params,
      }),
    }

    // URLSearchParams stringifies non-string values itself; this does the same explicitly.
    const query = new URLSearchParams(
      Object.entries(params).map(([key, value]) => [key, String(value)])
    )
    this.href = this.href + '?' + query.toString()
    return this
  }
}

export function PathHelperProvider({ children }: { children?: ReactNode }) {
  const router = useRouter()
  const routesSignature = Object.keys(router.routesByPath).sort().join('\n')
  const paths = React.useMemo(() => {
    void routesSignature

    const built = new PathHelperBuilder(router).build()

    if (!hasHelperForEveryRoute(built, Object.keys(router.routesByPath))) {
      throw new Error('Path helpers are missing for some registered routes')
    }

    return built
  }, [router, routesSignature])

  return <PathHelperContext.Provider value={paths}>{children}</PathHelperContext.Provider>
}

// Only the route paths (keys) are read, so tests can pass a plain object of paths.
type PathHelperRouter = { routesByPath?: object }

export class PathHelperBuilder {
  router: PathHelperRouter

  constructor(router: PathHelperRouter) {
    this.router = router
  }

  build(): Record<string, PathHelper> {
    const routes = this.sortRoutes(Object.keys(this.router.routesByPath ?? {}))
    const paths: Record<string, PathHelper> = {}

    paths['root'] = new PathHelper('/', null)

    for (const route of routes) {
      const key = lastRouteSegment(route)

      // if the key starts with a dollar sign, it's a param
      // save for the next iteration and apply to the route segment
      if (!key || key.startsWith('$')) {
        continue
      }

      // if the key is empty or 'create', skip it
      if (key === 'create') {
        continue
      }

      const cleanKey = this.sanitizeKey(key)

      // if we've already created a path helper for this key, skip it
      if (paths[cleanKey]) {
        continue
      }

      paths[cleanKey] = new PathHelper(route, this.findDirectDollarParam(route, routes))
    }

    for (const [helperName, suffixes] of Object.entries(aliasedHelperBaseRouteSuffixes)) {
      const route = routes.find((candidate) =>
        suffixes.some((suffix) => candidate.endsWith(suffix))
      )

      if (route) {
        paths[helperName] = new PathHelper(route, this.findDirectDollarParam(route, routes))
      }
    }

    return paths
  }

  sortRoutes(routes: readonly string[]): string[] {
    return [...routes].sort((a, b) => {
      const aPreferred = this.isPreferredRoute(a)
      const bPreferred = this.isPreferredRoute(b)

      if (aPreferred !== bPreferred) {
        return aPreferred ? -1 : 1
      }

      return b.localeCompare(a)
    })
  }

  isPreferredRoute(route: string): boolean {
    const key = lastRouteSegment(route)

    if (!key || key.startsWith('$')) {
      return false
    }

    const preferredSuffixes = preferredHelperBaseRouteSuffixes[this.sanitizeKey(key)] ?? []

    return preferredSuffixes.some((suffix) => route.endsWith(suffix))
  }

  // make path names useable as object keys
  sanitizeKey(key: string): string {
    return sanitizePathHelperKey(key)
  }

  findDirectDollarParam(route: string, routes: readonly string[]): string {
    const baseSegments = route.split('/').filter(Boolean)

    for (const candidateRoute of routes) {
      if (candidateRoute === route) {
        continue
      }

      const candidateSegments = candidateRoute.split('/').filter(Boolean)

      if (candidateSegments.length !== baseSegments.length + 1) {
        continue
      }

      const sharesBasePath = baseSegments.every(
        (segment, index) => candidateSegments[index] === segment
      )

      if (!sharesBasePath) {
        continue
      }

      const directChildSegment = candidateSegments.at(-1)

      if (directChildSegment?.startsWith('$')) {
        return directChildSegment
      }
    }

    return ''
  }
}

function lastRouteSegment(route: string): string | undefined {
  return route.split('/').filter(Boolean).pop()
}

export function sanitizePathHelperKey(key: string): string {
  return key.replace(/(-\w)/g, (match) => match.toUpperCase().replace('-', ''))
}

// The helper keys build() creates for the registered router, derived with the same rule at the type
// level: the last non-empty route segment, skipping $params and `create`, camelCased.
// usePathHelper.test.tsx checks the type-level rule against sanitizePathHelperKey.
type AppRoutePath = keyof RegisteredRouter['routesByPath'] & string

type LastSegment<P extends string> = P extends `${infer Head}/`
  ? LastSegment<Head>
  : P extends `${string}/${infer Rest}`
    ? LastSegment<Rest>
    : P

export type CamelCasePathSegment<S extends string> = S extends `${infer Head}-${infer Tail}`
  ? `${Head}${CamelCasePathSegment<Capitalize<Tail>>}`
  : S

export type PathHelperKeyForRoute<P extends string> =
  LastSegment<P> extends '' | 'create' | `$${string}` ? never : CamelCasePathSegment<LastSegment<P>>

type RouteHelperKey = { [P in AppRoutePath]: PathHelperKeyForRoute<P> }[AppRoutePath]

type AliasHelperKey = {
  [K in keyof typeof aliasedHelperBaseRouteSuffixes]: [
    Extract<AppRoutePath, `${string}${(typeof aliasedHelperBaseRouteSuffixes)[K][number]}`>,
  ] extends [never]
    ? never
    : K
}[keyof typeof aliasedHelperBaseRouteSuffixes]

export type PathHelperKey = RouteHelperKey | AliasHelperKey | 'root'

export type PathHelpers = Readonly<Record<PathHelperKey, PathHelper>>

// build() applies the rule above to the router's real route paths. Re-derive each expected key from
// those paths and confirm its helper exists before exposing the map with typed keys.
function hasHelperForEveryRoute(
  paths: Record<string, PathHelper>,
  routes: readonly string[]
): paths is PathHelpers {
  const routeKeys = routes.flatMap((route) => {
    const key = lastRouteSegment(route)

    return !key || key.startsWith('$') || key === 'create' ? [] : [sanitizePathHelperKey(key)]
  })
  const aliasKeys = Object.entries(aliasedHelperBaseRouteSuffixes).flatMap(([key, suffixes]) =>
    routes.some((route) => suffixes.some((suffix) => route.endsWith(suffix))) ? [key] : []
  )

  return ['root', ...routeKeys, ...aliasKeys].every((key) => paths[key] instanceof PathHelper)
}

// PathHelper class that encapsulates all command functionality
export class PathHelper {
  basePath: string
  dollarParam: string | null
  route: string
  paramCount: number
  routeData: { indexes: Record<string, number>; paramCount: number }

  constructor(basePath: string, dollarParam: string | null) {
    this.basePath = basePath
    this.dollarParam = dollarParam
    this.route = dollarParam ? this.joinPath(basePath, dollarParam) : basePath
    this.paramCount = this.getParamCount(this.route)
    this.routeData = this.indexRoute(this.route)

    // bind methods to preserve 'this' context for destructuring (js is dumb)
    this.link = this.link.bind(this)
    this.href = this.href.bind(this)
    this.index = this.index.bind(this)
    this.create = this.create.bind(this)
    this.show = this.show.bind(this)
    this.edit = this.edit.bind(this)
    this.delete = this.delete.bind(this)
  }

  getParamCount(route: string): number {
    return route.split('/').filter((s) => s.startsWith('$')).length
  }

  indexRoute(route: string): { indexes: Record<string, number>; paramCount: number } {
    const segments = route.split('/')
    const indexes: Record<string, number> = {}
    let paramCount = 0

    segments.forEach((segment, i) => {
      if (segment.startsWith('$')) {
        indexes[segment.replace(/^\$/, '')] = i
        paramCount++
      }
    })

    return { indexes, paramCount }
  }

  joinPath(basePath: string, pathname: string | null | undefined): string {
    if (!pathname) {
      return basePath || '/'
    }

    const cleanBasePath = basePath === '/' ? '' : String(basePath).replace(/\/+$/, '')
    const cleanPathname = String(pathname).replace(/^\/+/, '')

    return `${cleanBasePath}/${cleanPathname}`
  }

  replaceParams(pathname: string, params: RouteParams): string {
    for (const [param, value] of Object.entries(params)) {
      pathname = pathname.replace(`$${param}`, String(value))
    }
    return pathname
  }

  parseParams(
    pathname: string,
    routeParams: readonly (RouteParamValue | undefined)[],
    existingParams?: RouteParams
  ): [RouteParams, number] {
    let paramCount = 0

    const params: RouteParams = { ...existingParams }

    const pathSegments = pathname.split('/').filter(Boolean)

    if (pathSegments.length === 0) {
      return [params, 0]
    }

    let paramIndex = 0

    // this loops through the route segments
    // finds param segments
    // stores the param name (key) and value (value) in the params object
    // pathname =/settings/locations/$locationsId/delivery-details/$deliveryId
    // routeParams = [1, 2]
    // {locationsId: 1, deliveryId: 2}
    for (const segment of pathSegments) {
      if (segment.startsWith('$')) {
        paramCount++

        if (paramIndex >= routeParams.length) {
          throw new Error(
            `Missing required route parameter: ${segment}. Expected ${routeParams.length} parameters but got ${paramIndex}.`
          )
        }

        const paramName = segment.slice(1)
        params[paramName] = routeParams[paramIndex]
        paramIndex++
      }
    }

    return [params, paramCount]
  }

  link(pathname = '', ...routeParams: (RouteParamValue | undefined)[]): PathLink {
    const [params] = this.parseParams(pathname, routeParams)
    const to = this.joinPath(this.basePath, pathname)
    return { to, params }
  }

  href(pathname = '', ...routeParams: (RouteParamValue | undefined)[]): string {
    const paramCount = this.getParamCount(this.route)
    const neededParamCount = paramCount + (pathname.startsWith('$') ? 1 : 0)

    // when we're using cached tanstack params, not everything we need
    // to build the href is passed in.  this uses indexed params in the route and pulls them
    // from the location.pathname.
    const existingParams: RouteParams = {}
    if (neededParamCount > routeParams.length) {
      const locationSegments = location.pathname.split('/')
      const pathSegements = this.joinPath(this.basePath, pathname).split('/')

      for (const pathSegment of pathSegements) {
        if (pathSegment.length > 0 && pathSegment.startsWith('$')) {
          const paramName = pathSegment.replace(/^\$/, '')
          const index = this.routeData.indexes[paramName]

          if (index) {
            existingParams[paramName] = locationSegments[index]
          }
        }
      }
    }

    const [parsed] = this.parseParams(pathname, routeParams, existingParams)
    const to = this.joinPath(this.basePath, pathname)

    return this.replaceParams(to, parsed)
  }

  index(): PathResult {
    return new PathResult(this.link(), this.href(), this.route)
  }

  create(): PathResult {
    return new PathResult(this.link('create'), this.href('create'), this.route)
  }

  show(paramValue: RouteParamValue | undefined): PathResult {
    if (!this.dollarParam) {
      throw new Error('show() method requires a dollar parameter route')
    }
    return new PathResult(
      this.link(this.dollarParam, paramValue),
      this.href(this.dollarParam, paramValue),
      this.route
    )
  }

  edit(paramValue: RouteParamValue): PathResult {
    if (!this.dollarParam) {
      throw new Error('edit() method requires a dollar parameter route')
    }
    const editPath = this.joinPath(this.dollarParam, 'edit')

    return new PathResult(
      this.link(editPath, paramValue),
      this.href(editPath, paramValue),
      this.route
    )
  }

  delete(paramValue: RouteParamValue): PathResult {
    if (!this.dollarParam) {
      throw new Error('delete() method requires a dollar parameter route')
    }
    return new PathResult(
      this.link(this.dollarParam, paramValue),
      this.href(this.dollarParam, paramValue),
      this.route
    )
  }
}
